import DIContainer from '@components/DependencyInjectionContainer.js';
import { combineLatest, filter, from, fromEvent, interval, merge, of, Subject, switchMap, take } from 'rxjs';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
import { ENV } from '@components/Environment.js';
import { LoginFormOverlay } from '@components/Component/Container/Form/LoginForm/LoginFormOverlay/LoginFormOverlay.js';
import { ScreenManager } from '@components/Component/Container/ScreenManager/ScreenManager.js';
import { getCookie, getAccessTokenCookieName, parseJWT } from '@components/Utility/index.js';
export class SessionTimeoutManager {
    constructor() {
        this.timerInitialized = false;
        this.expiryTime = 0;
        this.sessionExpired = false;
        this.warningThresholdMillis = 0;
        this.lastPulse = 0;
        this.expiry$ = new Subject();
        this.warningThreshold$ = new Subject();
        this.pollingSubscription = null;
        this.titleToggleInterval = null;
        this._sessionRestored$ = new Subject();
        // private debugTimer() {
        // 	if (this.debugInterval) {
        // 		clearInterval(this.debugInterval)
        // 	}
        //
        // 	const now = Date.now(); // this will suffice to get the Epoch
        // 	const warningTime = this.expiryTime - this.warningThresholdMillis
        // 	const delay = warningTime - now;
        //
        // 	console.log('start debugging timer', delay)
        // 	const startTime = Date.now();
        // 	this.debugInterval = setInterval(() => {
        // 		const elapsed = Date.now() - startTime;
        // 		const remaining = delay - elapsed;
        // 		if (remaining <= 0) {
        // 			console.log('Showing warning!!!!');
        // 			clearInterval(this.debugInterval);
        // 		} else {
        // 			console.log(`Show warning in: ${Math.ceil(remaining / 1000)} seconds`);
        // 		}
        // 	}, 1000);
        // }
    }
    get sessionRestored$() {
        return this._sessionRestored$;
    }
    get ScreenManager() {
        return DIContainer.getInstance('ScreenManager');
    }
    get BroadcastManager() {
        return DIContainer.getInstance('BroadcastManager');
    }
    get Application() {
        return DIContainer.getInstance('Application');
    }
    get LoginFormOverlay() {
        if (!this._loginFormOverlay) {
            this._loginFormOverlay = DIContainer.new('LoginFormOverlay', LoginFormOverlay);
        }
        return this._loginFormOverlay;
    }
    /*** These are only intended for debugging purposes and should always be committed as false  **/
    // private debugging: boolean = true
    // private debugInterval: any = null;
    /*** **************************************************************************************** */
    /**
     * Sets the expiry time in milleseconds. Which is in sync with the Refresh Token expiry.
     * This will update every time the Access Token expires and a request occurs - which updates the Refresh token.
     * @param expiry
     */
    setExpiry(expiry) {
        if (!this.timerInitialized) {
            this.initializeTimer();
        }
        if (this.expiryTime < expiry) {
            this.expiry$.next(expiry);
        }
    }
    /**
     * Returns the current Expiry as Epoch
     */
    getExpiry() {
        return this.expiryTime;
    }
    /**
     * Sets the warning threshold which is the amount of time prior to the expiry time that the warning will appear.
     * This value comes from SESSIONSTIMEOUT configvalue2 and is minutes which get converted to milliseconds
     * It gets loaded and set once during the application load and never again.
     * @param threshold
     */
    setWarningThreshold(threshold) {
        if (this.warningThresholdMillis !== 0) {
            return; // This should only be called once on application load and never again
        }
        if (!this.timerInitialized) {
            this.initializeTimer();
        }
        this.warningThreshold$.next(threshold);
    }
    /**
     * Returns whether or not the current session expired
     */
    isExpired() {
        return this.sessionExpired;
    }
    resetExpiredState() {
        this.clearPoll();
        this.sessionExpired = false;
        if (this.warningAlert) {
            this.warningAlert.cancel();
            this.warningAlert = undefined;
        }
        if (this.titleToggleInterval) {
            clearInterval(this.titleToggleInterval);
            this.titleToggleInterval = null;
        }
        this.sessionRestoredSub?.unsubscribe();
        this.sessionRestoredSub = undefined;
        this.broadcastRestoredSub?.unsubscribe();
        this.broadcastRestoredSub = undefined;
        if (this._loginOverlayCoverRef) {
            this._loginOverlayCoverRef.unloadForm();
            this.ScreenManager.screenRemove(this._loginOverlayCoverRef.id);
            this._loginOverlayCoverRef = undefined;
        }
        if (this.originalTitle) {
            document.title = this.originalTitle;
        }
    }
    pulse() {
        const now = Date.now();
        if (!!this.lastPulse && now - this.lastPulse < 5000) {
            return;
        }
        this.lastPulse = now;
        // Native apps don't use cookies; pulse() is only relevant for browser sessions
        if (ENV('isNative'))
            return;
        if (!getCookie(getAccessTokenCookieName()) && !this.sessionExpired) {
            if (this.warningAlert) {
                this.warningAlert.cancel();
                this.warningAlert = undefined;
            }
            this.expiryRequest().subscribe();
        }
    }
    /**
     * Clears any set timers
     */
    clearPoll() {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
        }
        // if (this.debugInterval) {
        // 	clearInterval(this.debugInterval)
        // }
    }
    handleSessionTimeout() {
        // Clear the invalid access token from secure storage so subsequent requests
        // only send the refresh token (which the server can use to issue a new access token).
        if (ENV('isNative')) {
            DIContainer.getInstance('AppStorageManager')
                .remove('accessToken', true)
                .catch((e) => console.warn('Failed to clear access token from secure storage', e));
        }
        if (ENV('loginOverlay')) {
            this.showLoginOverlay();
        }
        else {
            // send to log in screen
            this.Application.logout();
        }
    }
    /**
     * Shows the Login overlay and updates the document title
     */
    showLoginOverlay() {
        if (this.ScreenManager.screenCoverActive) {
            return;
        }
        this.clearPoll();
        this.expiryTime = 0; // reset the expiry so the login request will request a new one
        this.sessionExpired = true;
        const timeoutTitle = DIContainer.getInstance('CaptionManager').getCaption('system_session_timedout_title');
        document.title = timeoutTitle;
        const screenManager = DIContainer.getInstance('ScreenManager', ScreenManager);
        const loginFormOverlay = DIContainer.new('LoginFormOverlay');
        this.warningAlert?.cancel();
        this.warningAlert = undefined;
        this.sessionRestoredSub?.unsubscribe();
        this.broadcastRestoredSub?.unsubscribe();
        // listen for login completion (password form or OAuth popup)
        this.broadcastRestoredSub = this.BroadcastManager.listen('session-restored').pipe(take(1), 
        // Get the new access token: from secure storage (native) or cookie (browser)
        switchMap((resp) => {
            const accessToken$ = ENV('isNative')
                ? from(DIContainer.getInstance('AppStorageManager')
                    .getString('accessToken', true))
                : of(getCookie(getAccessTokenCookieName()) || null);
            return accessToken$.pipe(switchMap((accessToken) => {
                const { source } = resp.payload;
                const sessionUserID = ENV('userId');
                if (!accessToken) {
                    console.log('Session restore failed - no access token found');
                    return of(void 0);
                }
                const payload = parseJWT(accessToken);
                const newUserId = payload?.user_id;
                // For OAuth login, ENV('userId') may not be set yet
                if (source === 'oauth' && newUserId) {
                    ENV('userId', newUserId);
                }
                if (sessionUserID && newUserId !== sessionUserID) {
                    this.Application.logout();
                    return of(void 0);
                }
                if (source === 'oauth') {
                    // making this request returns the x-refresh-expiry header which we need to reset the expiry timer
                    return this.expiryRequest().pipe(switchMap(() => {
                        this.sessionRestored$.next(true);
                        return of(void 0);
                    }));
                }
                this.sessionRestored$.next(true);
                return of(void 0);
            }));
        })).subscribe();
        const screenCover = screenManager.open(loginFormOverlay, { screenCover: true });
        this._loginOverlayCoverRef = screenCover;
        this.sessionRestoredSub = this.sessionRestored$
            .subscribe(() => {
            screenCover.unloadForm();
            this.ScreenManager.screenRemove(screenCover.id);
            this._loginOverlayCoverRef = undefined;
            this.sessionExpired = false;
            if (this.originalTitle) {
                document.title = this.originalTitle;
            }
            else {
                document.title = 'Q360';
            }
        });
    }
    initializeTimer() {
        combineLatest([
            this.expiry$,
            this.warningThreshold$
        ]).subscribe(([expiry, threshold]) => {
            this.expiryTime = expiry;
            this.warningThresholdMillis = (threshold * 60 * 1000);
            this.sessionExpired = false;
            this.scheduleWarning();
        });
        this.BroadcastManager.listen('expiry-updated')
            .subscribe((resp) => {
            const { expiry } = resp.payload;
            this.setExpiry(expiry);
        });
        this.timerInitialized = true;
    }
    /**
     * Schedules the modal to show based on the expiry time and warning threshold
     * @private
     */
    scheduleWarning() {
        if (this.pollingSubscription) {
            this.clearPoll();
        }
        if (this.warningAlert) {
            this.warningAlert.cancel();
            this.warningAlert = undefined;
            if (this.originalTitle) {
                document.title = this.originalTitle;
            }
        }
        console.log('Scheduling New Warning. Current Time: ' + new Date().toString() + ' Popup time: ' + new Date(this.expiryTime - this.warningThresholdMillis).toString() + ' Session Expiry: ' + new Date(this.expiryTime).toString());
        this.pollingSubscription = this.setupPoll().subscribe(() => {
            this.checkAndShowWarning();
        });
    }
    setupPoll() {
        const poll$ = interval(10000); // 10 second polling interval
        const visibility$ = fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'));
        return merge(poll$, visibility$);
    }
    /**
     * Checks the current time against the warning threshold and will show the warning if the time has elapsed
     * @private
     */
    checkAndShowWarning() {
        const now = Date.now(); // this will suffice to get the Epoch
        const warningTime = this.expiryTime - this.warningThresholdMillis;
        const delay = warningTime - now;
        // console.log('Checking for timeout. Now: ' +
        // 		new Date(now).toString()
        // 		+ 'Warning Popup Time: ' +
        // 		new Date(warningTime).toString()
        // 		+ ' Current Delay Seconds:  ' + (delay / 1000))
        if (delay <= 0) {
            this.clearPoll();
            this.showWarning();
        }
    }
    showWarning() {
        //console.log('%cTIMEOUT WARNING', 'font-size:24pt;color:gold;')
        const timeoutWarningTitle = DIContainer.getInstance('CaptionManager').getCaption('system_session_timeout_idle_warning');
        const originalTitle = this.originalTitle = document.title;
        let toggle = false;
        const expiryTimeString = DateTimeManager.formatDateTime(DateTimeManager.fromEpoch(this.expiryTime, true), ENV('timeFormat'), 'utc', 'USER');
        if (this.titleToggleInterval) {
            clearInterval(this.titleToggleInterval);
        }
        // toggle the tab title
        this.titleToggleInterval = setInterval(() => {
            document.title = toggle ? originalTitle : timeoutWarningTitle;
            toggle = !toggle;
        }, 1000);
        this.pollingSubscription = this.setupPoll().subscribe(() => {
            const now = Date.now();
            if (now > this.expiryTime) {
                this.clearPoll();
                if (this.titleToggleInterval) {
                    clearInterval(this.titleToggleInterval);
                }
                this.handleSessionTimeout();
            }
        });
        this.warningAlert = DIContainer.new('Alert');
        this.warningAlert.attachModal(document.body);
        this.warningAlert.open({
            captionKey: 'system_session_timeout',
            captionKeyTokens: { expirytime: expiryTimeString },
            submitCaptionKey: 'system_extendsession',
            modalStyle: 'warning'
        })
            .pipe(switchMap(() => {
            if (this.sessionExpired) {
                return of(true);
            }
            if (this.titleToggleInterval) {
                clearInterval(this.titleToggleInterval);
                document.title = originalTitle;
            }
            this.warningAlert = undefined;
            return this.expiryRequest();
        }))
            .subscribe();
    }
    expiryRequest() {
        return DIContainer.getInstance('Request').GET({
            endpoint: 'authenticate',
            headers: { 'X-Request-Refresh-Token-Expiry': 'true' }
        });
    }
}
